import type { CameraGuard } from './cameraGuard';
import type { PropertyFiltersApi, PropertySearchScope } from './context';
import type { PropertiesListingMapState } from '~/composables/useMapListing';
import type { Coordinates } from '~/core/entities/property/coordinates';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { Router } from 'vue-router';

import { initializeMapState } from '~/composables/useMapListing';
import {
	toFiniteCoordinates,
	toTruthyCoordinates
} from '~/core/entities/property/coordinates';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

import {
	createTrackedCameraGuard,
	createUntrackedCameraGuard
} from './cameraGuard';
import { usePropertySearchScope, usePropertySearchStateKey } from './context';

type ClustersUpdate = (filters: PropertyFilters) => void;
export interface MapPageConfig {
	page: 'imoveis' | 'colecoes';
	collection?: string;
	wishlistId?: string;
}

// Everything the map controller does differently between the lab and production.
export interface MapSearchMode {
	cameraStateKey: string;
	initialCameraState(): Pick<PropertiesListingMapState, 'mapConfig'>;
	createCamera(): CameraGuard;
	toPoi(lat?: number, lng?: number): Coordinates | undefined;
	acceptsViewport(camera: CameraGuard, origin?: 'control'): boolean;
	refreshWithoutWrite(updateClusters: ClustersUpdate): void;
	onViewportComputed(filters: PropertyFilters, update: ClustersUpdate): void;
	writeViewport(
		filters: PropertyFilters,
		page: MapPageConfig
	): Promise<unknown>;
	resetSelection(filters: PropertyFilters, force: ClustersUpdate): unknown;
}

// Clusters follow the URL through the search watcher, so the map only writes intents.
export function createLabMapMode(scope: PropertySearchScope): MapSearchMode {
	return {
		cameraStateKey: scope.stateKey('properties-listing-camera'),
		initialCameraState: () => ({ mapConfig: initializeMapState().mapConfig }),
		createCamera: createTrackedCameraGuard,
		toPoi: toFiniteCoordinates,
		acceptsViewport: (camera, origin) =>
			camera.isGesture.value || origin === 'control',
		refreshWithoutWrite: update =>
			update({ ...scope.search.confirmedFilters.value }),
		onViewportComputed: () => {},
		writeViewport: filters => scope.actions.moveMap(filters),
		resetSelection: filters => scope.actions.clearAreaSelection(filters)
	};
}

function locationContext(filters: PropertyFilters) {
	return {
		regions: filters.regions,
		cities: filters.cities,
		states: filters.states,
		macroregions: filters.macroregions
	};
}

// Production behaviour, kept as it was: the map pushes URLs and refreshes clusters itself.
export function createLegacyMapMode(deps: {
	router: Pick<Router, 'push'>;
	api: Pick<PropertyFiltersApi, 'getPathFromFilters'>;
	stateKey: string;
}): MapSearchMode {
	const push = (filters: PropertyFilters, page: MapPageConfig) =>
		deps.router.push(
			deps.api.getPathFromFilters(filters, locationContext(filters), page)
		);
	return {
		cameraStateKey: deps.stateKey,
		initialCameraState: initializeMapState,
		createCamera: createUntrackedCameraGuard,
		toPoi: toTruthyCoordinates,
		acceptsViewport: () => true,
		refreshWithoutWrite: () => {},
		onViewportComputed: (filters, update) => update(filters),
		writeViewport: push,
		resetSelection(filters, force) {
			void push(filters, { page: 'imoveis' });
			force(filters);
		}
	};
}

export function useMapSearchMode(): MapSearchMode {
	const scope = usePropertySearchScope();
	if (scope) return createLabMapMode(scope);
	return createLegacyMapMode({
		router: useRouter(),
		api: PropertyFiltersPresenter,
		stateKey: usePropertySearchStateKey('properties-listing-map')
	});
}
